// EnterInfoStep
import { useState, useEffect, JSX, useMemo, useRef, useCallback } from "react";
import { Loader2, RotateCw, Search, User } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "flag-icons/css/flag-icons.min.css";
import { useLanguage } from "../i18n/LanguageContext";

import {
  BookingInfo,
  CreateTicketCommand,
  LookupsClient,
  PatientInfo,
  RevisitDto,
  SearchRevisitQuery,
  TicketsClient,
} from "../web-api-client";
import Alert from "../components/ui/Alert";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import FormSelect, {
  OptionType,
} from "../components/booking/information/FormSelect";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useToast } from "../hooks/use-toast";
import { useSessionStorageState } from "../hooks/useSessionStorageState";
import { useSuccessNotification } from "../hooks/use-success-notification";
import DatePickerCustom from "../components/ui/DatePicker";
import { RHFMobileSelectSheet } from "../components/booking/information/RHFMobileSelectSheet";
import { format, parse } from "date-fns";
import LoadingIndicator from "../components/LoadingIndicator";
import { useStickyLoading } from "../hooks/useStickyLoading";
import {
  useDistrictOptions,
  useLookupBundle,
  useWardOptions,
} from "../hooks/useLookupData";
import NotifyDialog from "../components/ui/NotifyDialog";
import { toUserMessage } from "../utils/toUserMessage";
import QrScanButton from "../components/booking/information/QrScanButton";
import { CitizenQRInfo } from "../types/citizenQRInfo";
import {
  convertDobToYMD,
  getValueByLabel,
  mapGender,
  splitVnAddress,
} from "../utils/map";
import { useSuggestedJobValue } from "../hooks/useAutoJobFromDob";
import { isCCCD, isCMND, isVNPassport } from "../utils/nationId";
import { appConfig } from "../appconfig";
import { useBookingSettings } from "../hooks/useBookingSettings";
import BookingTimeoutDialog from "../components/booking/BookingTimeoutDialog";
import { useBookingTimeGuard } from "../hooks/useBookingTimeGuard";

interface FormValues {
  name: string;
  dob: string;
  phone: string;
  address?: string;
  idNumber?: string;
  ethnicity: string;
  job: string;
  gender: string;
  city: string;
  ward: string;
  lydokham: string;
  captchaInput: string;
}

type Flow = "byTime" | "byDoctor" | undefined;

interface Props {
  isMobile: boolean;
}

function EnterInfoStep({ isMobile }: Props): JSX.Element {
  const { t } = useLanguage();

  const { checkBookingTime, dialogOpen, dialogType, closeDialog } =
      useBookingTimeGuard();
      
  const ExamineSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(t("vulongnhaphoten")),
        dob: yup
          .string()
          .required(t("vulongchonngaysinh"))
          .matches(
            /^\d{4}\/(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])$/,
            t("ngaysinhkhonghople"),
          )
          .test("is-valid-date", t("ngaysinhkhonghople"), (value) => {
            if (!value) return false;
            const [year, month, day] = value.split("/").map(Number);
            const date = new Date(year, month - 1, day);
            return (
              date.getFullYear() === year &&
              date.getMonth() === month - 1 &&
              date.getDate() === day
            );
          })
          .test("year-less-than-now", t("namsinhphainhohonn"), (value) => {
            if (!value) return false;
            const year = parseInt(value.split("/")[0], 10);
            const currentYear = new Date().getFullYear();
            return year < currentYear;
          }),
        phone: yup
          .string()
          .transform((value) => {
            if (value?.startsWith("+84")) {
              return "0" + value.slice(3);
            }
            return value;
          })
          .required(t("vulongnhapsdt"))
          .matches(
            /^0(3[2-9]|5[6|8|9]|7[06-9]|8[1-5]|8[8-9]|9[0-9])\d{7}$/,
            t("sdtkhonghople"),
          ),
        job: yup.string().required(t("vulongchonnghenghiep")),
        gender: yup.string().required(t("vulongchongioitinh")),
        // city: yup.string().required(t("vulongchontinhthanh")),
        // ward: yup.string().required(t("vulongchonphuongxa")),
        ethnicity: yup.string().required(t("vulongchondntoc")),
        lydokham: yup.string().required(t("chuanhaplydokham")),
        captchaInput: yup.string().required(t("vulongnhapcaptcha")),
        idNumber: yup
          .string()
          .optional()
          .nullable()
          // chuẩn hoá: uppercase + chỉ giữ A-Z/0-9
          .transform((v) =>
            (v ?? "")
              .toString()
              .toUpperCase()
              .replace(/[^A-Z0-9]/g, ""),
          )
          .max(12, t("toida12kytu"))
          .test("id-auto-detect", t("cccd12cmnd9"), (v) => {
            const s = (v ?? "").trim();
            // BỎ TRỐNG → PASS
            if (s.length === 0) return true;
            // CÓ NHẬP → kiểm 1 trong 3 pattern
            return isCCCD(s) || isCMND(s) || isVNPassport(s);
          }),
        address: yup.string().optional().nullable(),
      }),
    [t],
  );

  const genderOptions = useMemo(
    () => [
      { value: "M", label: t("nam") },
      { value: "F", label: t("nu") },
    ],
    [t],
  );

  const {
    register,
    control,
    handleSubmit,
    resetField,
    formState: { errors },
    setValue,
    getValues,
    trigger,
    watch,
  } = useForm<FormValues>({
    resolver: yupResolver(ExamineSchema as any),
    mode: "onChange",
    reValidateMode: "onChange",
    shouldFocusError: true,
    defaultValues: {
      captchaInput: "bypass",
    },
  });

  const captchaScope = "booking";

  const notificationConfig = {
    title: t("datkhamthanhcong"),
    message: t("phieukhamdaluu"),
  };

  const { toast } = useToast();
  const { notify } = useSuccessNotification();

  const [searchParams] = useSearchParams();

  const [selectedDoctor] = useSessionStorageState("selectedDoctor", null);
  const [selectedTime] = useSessionStorageState("selectedTime", null);
  const [selectedClinic] = useSessionStorageState("selectedClinic", null);
  const [selectedDate] = useSessionStorageState("selectedDate", null);
  const [selectedMonth] = useSessionStorageState("selectedMonth", null);
  const [selectedYear] = useSessionStorageState("selectedYear", null);
  const [orgToken] = useSessionStorageState("orgLinkContext", null);

  const navigate = useNavigate();

  //todo: chuyen sang session storage
  let clinicId = Number(searchParams.get("specialty"));
  let doctorSlotId = Number(searchParams.get("blockId"));
  const type = searchParams.get("type") ?? undefined;
  const flow = (searchParams.get("flow") ?? undefined) as Flow;

  const isPresent = (v: unknown) =>
    v !== null && v !== undefined && (typeof v !== "string" || v.trim() !== "");

  const isBookingValid = useMemo(() => {
    const base = [selectedTime, selectedDate, selectedMonth, selectedYear];

    if (type === "clinic") return [selectedClinic, ...base].every(isPresent);

    if (type === "doctor") return [selectedDoctor, ...base].every(isPresent);

    return base.every(isPresent);
  }, [
    flow,
    selectedDoctor,
    selectedTime,
    selectedClinic,
    selectedDate,
    selectedMonth,
    selectedYear,
  ]);

  const [activeTab, setActiveTab] = useState<"new" | "examined">("new");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [captchaInputError, setCaptchaInputError] = useState<string | null>(
    null,
  );
  const [patientCodeError, setPatientCodeError] = useState<string | null>(null);
  const [captchaUrl, setCaptchaUrl] = useState<string>(
    `${appConfig.apiBaseUrl}/api/captcha/${captchaScope}`,
  );
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [provinceId, setProvinceId] = useState<number>();
  const [wardName, setWardName] = useState<string | undefined>();

  const { data: wardOptions = [], isFetching: wardsLoading } =
    useWardOptions(provinceId);

  const { data: lookupData, isFetching: lookupsLoading } =
    useLookupBundle(true);

  const isLoading = wardsLoading || lookupsLoading;
  const overlayVisible = useStickyLoading(isLoading, {
    showDelay: 150,
    minVisible: 300,
  });

  const jobOptions: OptionType[] = useMemo(
    () =>
      (lookupData?.jobs ?? []).map((job) => ({
        value: job.id ?? "",
        label: job.name ?? "",
      })),
    [lookupData?.jobs],
  );

  const ethnicOptions: OptionType[] = useMemo(
    () =>
      (lookupData?.ethnicities ?? []).map((ethnic) => ({
        value: ethnic.id ?? "",
        label: ethnic.name ?? "",
      })),
    [lookupData?.ethnicities],
  );
  const provinceOptions: OptionType[] = useMemo(
    () =>
      (lookupData?.provinces ?? []).map((province) => ({
        value: province.id ?? "",
        label: province.name ?? "",
      })),
    [lookupData?.provinces],
  );

  const [searchForm, setSearchForm] = useState({
    patientCode: "",
    patientName: "",
    captcha: "",
  });

  const [searchResult, setSearchResult] = useState<RevisitDto | null>(null);

  const dob = watch("dob");
  const job = watch("job");

  const { data: bookingSettings } = useBookingSettings();

  const suggestedJob = useSuggestedJobValue({
    dob,
    occupationOptions: jobOptions,
    rules: bookingSettings?.ageToOccupationRules ?? [],
    mode: "value",
  });

  const reloadCaptcha = () => {
    setCaptchaUrl(
      `${appConfig.apiBaseUrl}/api/captcha/booking?${new Date().getTime()}`,
    );
  };

  const handleSearchFormChange = (field: string, value: string) => {
    setSearchForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleGoHome = () => {
    sessionStorage.clear();

    navigate("/", { replace: true });
  };

  // Tìm kiếm hồ sơ khám lại
  const handleSearch = async () => {
    setErrorMessage(null);

    if (!searchForm.patientCode) {
      setPatientCodeError("Vui lòng nhập mã bệnh nhân hoặc mã số BHYT");
      return;
    } else {
      setPatientCodeError(null);
    }

    if (!searchForm.captcha) {
      setCaptchaInputError("Vui lòng nhập mã xác nhận");
      return;
    } else {
      setCaptchaInputError(null);
    }

    const client = new LookupsClient(appConfig.apiBaseUrl, {
      fetch: fetchWithTimeout(),
    });

    const query = new SearchRevisitQuery({
      patientCode: searchForm.patientCode,
      captcha: searchForm.captcha,
      captchaScope: captchaScope,
    });

    setSubmitting(true);

    try {
      const revisit = await client.searchRevisit(query);
      // const revisit = null;

      if (!revisit) {
        setErrorMessage(
          "Không tìm thấy hồ sơ khám. Vui lòng kiểm tra lại hoặc đăng ký khám mới.",
        );
        setSearchResult(null);
        return;
      }

      setSearchResult(revisit);
    } catch (error: any) {
      const errorMsg = await toUserMessage(error);

      toast({
        variant: "destructive",
        title: "Thất bại",
        description: errorMsg,
        duration: 3000,
      });

      cancelSearchResult();
      setErrorMessage(String(errorMsg));

      searchForm.captcha = ""; // không để ở finally
    } finally {
      setSubmitting(false);
      reloadCaptcha();
    }
  };

  // Hủy kết quả tìm kiếm hồ sơ
  const cancelSearchResult = () => {
    setSearchResult(null);
    setErrorMessage(null);
    searchForm.captcha = "";
    reloadCaptcha();
  };

  // submit cho đăng ký khám lại
  const handleSubmitExamined = () => {
    if (!selectedTime) return;

    if (type === "doctor") {
      if (!selectedClinic) return;

      clinicId = selectedClinic.clinicId;
    }

    const bookingInfo = new BookingInfo({
      clinicId: clinicId ?? selectedClinic.clinicId,
      doctorId: selectedDoctor?.doctorId ?? selectedDoctor?.id ?? "",
      appointmentDate: formatDate(selectedDate, selectedMonth, selectedYear),
      appointmentTime: selectedTime.range,
      timebandCode: selectedTime.timeband,
      reasonForVisit: "cntt test",
      scoped: type,
    });

    const patientInfo = new PatientInfo({
      fullName: searchResult?.fullName ?? "",
      dateOfBirth: searchResult?.dateOfBirth ?? "",
      gender: searchResult?.gender ?? "",
      nationalId: "001202000841",
      ethnicity: "1",
      occupation: "1",
      phoneNumber: "0886882792",
      provinceCode: "101",
      districtCode: "10101",
      wardCode: "1010103",
      addressDetail: "Số 1 ABC",
    });

    var captcha = searchForm.captcha;
    searchForm.captcha = "";

    submitTicket({
      captcha,
      bookingInfo,
      patientInfo,
    });
  };

  // submit cho đăng ký mới
  const onSubmit = (formData: FormValues) => {
    if (!selectedTime) return;

    var captcha = formData.captchaInput;

    let ethnic = formData.ethnicity ? String(formData.ethnicity) : "1";

    const bookingInfo = new BookingInfo({
      clinicId: clinicId ?? 0,
      scheduleSlotId: doctorSlotId ?? 0,
      doctorId: selectedDoctor?.doctorId ?? selectedDoctor?.id ?? "",
      appointmentDate: formatDate(selectedDate, selectedMonth, selectedYear),
      appointmentTime: selectedTime.range,
      timebandCode: selectedTime.timeband,
      reasonForVisit: formData.lydokham,
      scoped: type,
    });

    const patientInfo = new PatientInfo({
      fullName: formData.name,
      dateOfBirth: formatDateToDDMMYYYY(formData.dob),
      gender: formData.gender,
      nationalId: formData.idNumber,
      ethnicity: ethnic,
      occupation: formData.job,
      phoneNumber: formData.phone,
      provinceCode: formData.city ? formData.city + "" : undefined,
      wardCode: formData.ward ? formData.ward + "" : undefined,
      addressDetail: formData.address,
    });

    resetField("captchaInput", { defaultValue: "" });

    submitTicket({
      captcha,
      bookingInfo,
      patientInfo,
    });
  };

  const submitRef = useRef(false);
  const lydokhamRef = useRef<HTMLTextAreaElement | null>(null);
  const lydokhamRegister = register("lydokham");

  const submitTicket = async ({
    captcha,
    bookingInfo,
    patientInfo,
  }: {
    bookingInfo: BookingInfo;
    patientInfo: PatientInfo;
    captcha: string;
  }) => {
    if (submitting || submitRef.current) return;

    submitRef.current = true;
    setSubmitting(true);
    setErrorMessage(null);

    const client = new TicketsClient(appConfig.apiBaseUrl, {
      fetch: fetchWithTimeout(),
    });

    const command = new CreateTicketCommand({
      captcha,
      captchaScope,
      booking: bookingInfo,
      patient: patientInfo,
      organizationToken: orgToken ?? undefined,
    });

    try {
      const ticketId = await client.createTicket(command);

      notify(notificationConfig.title, notificationConfig.message);

      navigate(`/phieu-kham-benh/${ticketId}`, {
        state: {
          from: type,
          phone: patientInfo.phoneNumber,
          dateBooking: bookingInfo.appointmentDate,
        },
      });

      sessionStorage.clear();
    } catch (error: any) {
      const errorMsg = await toUserMessage(error);

      toast({
        variant: "destructive",
        title: t("thatbai"),
        description: errorMsg,
        duration: 3000,
      });

      cancelSearchResult();
      setErrorMessage(String(errorMsg));
    } finally {
      submitRef.current = false;
      setSubmitting(false);
      reloadCaptcha();
    }
  };

  const onProvinceSelected = (option?: OptionType) => {
    if (!option) return;

    const provinceId = Number(option.value);
    if (!provinceId) return;

    resetField("ward");

    setProvinceId(provinceId);
  };

  function formatDateToDDMMYYYY(dateStr: string): string {
    const date = new Date(dateStr);

    if (isNaN(date.getTime())) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // tháng bắt đầu từ 0
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  const formatDate = (
    day: number | string | null,
    month: number | string | null,
    year: number | string | null,
  ) => {
    if (!day || !month || !year) return "";

    const dd = String(day).padStart(2, "0");
    const mm = String(month).padStart(2, "0");
    const yyyy = String(year);

    return `${dd}/${mm}/${yyyy}`;
  };

  const handleDecoded = (info: CitizenQRInfo) => {
    const fullAddr = (info.address || "").replace(/\s+/g, " ").trim();
    const { detail, ward, district, city } = splitVnAddress(fullAddr);

    setValue("address", detail || "", {
      shouldDirty: true,
      shouldValidate: true,
    });

    var pId = getValueByLabel(provinceOptions, city, "province");

    setValue("city", String(pId) + "", {
      shouldDirty: true,
      shouldValidate: true,
    });

    setProvinceId(Number(pId));

    setWardName(ward);

    setValue("name", info.fullName);
    setValue("dob", convertDobToYMD(info.dob));
    setValue("idNumber", info.personalId);
    setValue("gender", mapGender(info.gender));

    trigger(["idNumber", "name", "gender", "dob", "address", "ward", "city"]);
  };

  useEffect(() => {
    document.title = t("nhaphosokham");

    reloadCaptcha();
  }, [t]);

  useEffect(() => {
    if (!suggestedJob) return;
    if (job) return;

    setValue("job", suggestedJob, {
      shouldDirty: true,
      shouldValidate: true,
    });
    trigger("job");
  }, [suggestedJob, job, setValue, trigger]);

  useEffect(() => {
    if (!provinceId) return;
    if (wardsLoading) return;
    if (!wardOptions.length) return;

    if (!wardName) return;

    const wid = getValueByLabel(wardOptions, wardName, "ward");
    if (wid == null) return;

    setValue("ward", String(wid), { shouldDirty: true, shouldValidate: true });

    setWardName(undefined);
  }, [provinceId, wardsLoading, wardOptions, wardName]);

  useEffect(() => {
    if (!ethnicOptions?.length) return;

    const cur = getValues("ethnicity");
    if (cur) return;

    setValue("ethnicity", String(ethnicOptions[0]?.value ?? ""), {
      shouldDirty: false,
      shouldValidate: true,
      shouldTouch: true,
    });
  }, [ethnicOptions, getValues, setValue]);

  useEffect(() => {
    if (
      errors.lydokham &&
      lydokhamRef.current &&
      !errors.name &&
      !errors.dob &&
      !errors.gender &&
      !errors.phone
    ) {
      lydokhamRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      setTimeout(() => {
        lydokhamRef.current?.focus();
      }, 50);
    }
  }, [errors.lydokham]);

  const toTitleCaseName = (value: string) => {
    return value
      .normalize("NFC")
      .toLocaleLowerCase("vi-VN")
      .trim()
      .replace(/\s+/g, " ")
      .split(" ")
      .map((word) => {
        if (!word) return word;
        return word.charAt(0).toLocaleUpperCase("vi-VN") + word.slice(1);
      })
      .join(" ");
  };

  return (
    <div>
      {overlayVisible && (
        <div className="fixed inset-0 z-[100] touch-none overscroll-contain">
          <div className="absolute inset-0 bg-white/40" aria-hidden="true" />
          <div className="relative w-full h-full flex items-center justify-center">
            <LoadingIndicator />
          </div>
        </div>
      )}
      {!isBookingValid && (
        <NotifyDialog
          title={t("phienkhonghople")}
          message={t("vulongchonbacsi")}
          buttonText={t("vetrangchu")}
          onButtonClick={handleGoHome}
        />
      )}
      <div className={`md:py-2 relative z-10 h-full`} id="form-info">
        <div className="bg-white md:rounded-lg shadow-md h-full">
          {/* Tab Navigation */}
          {/* <div className="flex border-b">
            <button
              onClick={() => {
                setActiveTab("new");
                setErrorMessage(null);
              }}
              className={`md:text-lg flex-1 py-3 px-6 text-center font-medium transition-colors duration-200 ${
                activeTab === "new"
                  ? "bg-green-600 text-white border-b-2 border-green-600"
                  : "text-gray-600 hover:text-green-600 hover:bg-green-50"
              }`}
            >
              Khám lần đầu
            </button>
            <button
              onClick={() => {
                setActiveTab("examined");
                setErrorMessage(null);
              }}
              className={`md:text-lg flex-1 py-3 px-6 text-center font-medium transition-colors duration-200 ${
                activeTab === "examined"
                  ? "bg-green-600 text-white border-b-2 border-green-600"
                  : "text-gray-600 hover:text-green-600 hover:bg-green-50"
              }`}
            >
              Khám lại
            </button>
          </div> */}

          <div className="bg-white h-full p-6 op-0 animate-slide-up-delay-1 mb-[60px]">
            {activeTab === "examined" ? (
              <div>
                {searchResult ? (
                  <div className={`${isMobile ? "pb-4" : ""} space-y-3`}>
                    {/* <Alert
                      variant="info"
                      msg={t("thongtinductimthay")}
                      className="mb-4 py-3"
                    />

                    <div className="op-0 animate-slide-up-delay-1">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div className="lg:col-span-1">
                          <label className="block text-base font-medium text-gray-700 mb-1">
                            {t("hovaten")}
                          </label>
                          <input
                            type="text"
                            className={`${
                              isMobile
                                ? "mobile-input-base-disabled"
                                : "input-base-small-disabled"
                            }`}
                            value={searchResult.fullName}
                            disabled={true}
                          />
                        </div>

                        <div className="lg:col-span-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-base font-medium text-gray-700 mb-1">
                                {t("ngaysinh")}
                              </label>
                              <input
                                type="text"
                                className={`${
                                  isMobile
                                    ? "mobile-input-base-disabled"
                                    : "input-base-small-disabled"
                                }`}
                                value={searchResult.dateOfBirth}
                                disabled={true}
                              />
                            </div>
                            <div>
                              <label className="block text-base font-medium text-gray-700 mb-1">
                                {t("gioitinh")}
                              </label>
                              <input
                                type="text"
                                className={`${
                                  isMobile
                                    ? "mobile-input-base-disabled"
                                    : "input-base-small-disabled"
                                }`}
                                value={searchResult.gender}
                                disabled={true}
                              />
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-base font-medium text-gray-700 mb-1">
                            {t("sodienthoai")}
                          </label>
                          <input
                            type="text"
                            className={`${
                              isMobile
                                ? "mobile-input-base-disabled"
                                : "input-base-small-disabled"
                            }`}
                            value={searchResult.phone}
                            disabled={true}
                          />
                        </div>

                        <div>
                          <label className="block text-base font-medium text-gray-700 mb-1">
                            Số CCCD/Hộ chiếu
                          </label>
                          <input
                            type="text"
                            className={`${
                              isMobile
                                ? "mobile-input-base-disabled"
                                : "input-base-small-disabled"
                            }`}
                            value={searchResult.nationalId}
                            disabled={true}
                          />
                        </div>

                        <div>
                          <label className="block text-base font-medium text-gray-700 mb-1">
                            Nghề nghiệp
                          </label>
                          <input
                            type="text"
                            className={`${
                              isMobile
                                ? "mobile-input-base-disabled"
                                : "input-base-small-disabled"
                            }`}
                            value={searchResult.occupationCode}
                            disabled={true}
                          />
                        </div>

                        <div>
                          <label className="block text-base font-medium text-gray-700 mb-1">
                            Dân tộc
                          </label>
                          <input
                            type="text"
                            className={`${
                              isMobile
                                ? "mobile-input-base-disabled"
                                : "input-base-small-disabled"
                            }`}
                            value={searchResult.ethnicityCode}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="op-0 animate-slide-up-delay-1">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-base font-medium text-gray-700 mb-1">
                            Tỉnh/Thành
                          </label>
                          <input
                            type="text"
                            className={`${
                              isMobile
                                ? "mobile-input-base-disabled"
                                : "input-base-small-disabled"
                            }`}
                            value={searchResult.provinceCode}
                            disabled={true}
                          />
                        </div>

                        <div>
                          <label className="block text-base font-medium text-gray-700 mb-1">
                            Phường/Xã
                          </label>
                          <input
                            type="text"
                            className={`${
                              isMobile
                                ? "mobile-input-base-disabled"
                                : "input-base-small-disabled"
                            }`}
                            value={searchResult.wardCode}
                            disabled={true}
                          />
                        </div>

                        <div className="lg:col-span-1">
                          <label className="block text-base font-medium text-gray-700 mb-1">
                            Số nhà/Tên đường
                          </label>
                          <input
                            type="text"
                            className={`${
                              isMobile
                                ? "mobile-input-base-disabled"
                                : "input-base-small-disabled"
                            }`}
                            value={searchResult.addressDetail}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>

                    {errorMessage && (
                      <Alert
                        variant="error"
                        msg={errorMessage}
                        className="mt-2.5"
                      />
                    )}

                    <div className="hidden md:grid grid-cols-1 md:grid-cols-2 gap-2 op-0 animate-slide-up-delay-2">
                      <button
                        className="order-2 md:order-1 w-full bg-gray-400 hover:bg-gray-450 text-white font-medium py-2.5 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                        onClick={cancelSearchResult}
                      >
                        <span>{t("quaylai")}</span>
                      </button>

                      <button
                        className="order-1 md:order-2 w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                        onClick={handleSubmitExamined}
                        disabled={submitting}
                      >
                        {submitting ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <div className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            <span>{t("dangkythongtin")}</span>
                          </div>
                        )}
                      </button>
                    </div> */}
                  </div>
                ) : (
                  <div className="space-y-4 bg-white">
                    <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">
                        {t("socccd")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={searchForm.patientCode}
                        onChange={(e) =>
                          handleSearchFormChange("patientCode", e.target.value)
                        }
                        type="text"
                        placeholder={t("nhapmabenhnhan")}
                        className={`${
                          !isMobile ? "input-base-small" : "mobile-input-base"
                        }`}
                      />
                      {patientCodeError && (
                        <p className="error-text">{patientCodeError}</p>
                      )}
                    </div>

                    {/* <div>
                      <label className="block text-base font-medium text-gray-700 mb-2">
                        Tên bệnh nhân
                      </label>
                      <input
                        value={searchForm.patientName}
                        onChange={(e) =>
                          handleSearchFormChange("patientName", e.target.value)
                        }
                        type="text"
                        placeholder="Tên bệnh nhân..."
                        className={`${
                          !isMobile ? "input-base-small" : "mobile-input-base"
                        }`}
                      />
                    </div> */}

                    {/* CaptchaComponent */}
                    <div className="mt-4">
                      <label className="block text-base font-medium text-gray-700 mb-2">
                        {t("maxacnhan")} <span className="text-red-500">*</span>
                      </label>

                      <div className="flex items-center gap-3">
                        <input
                          key="examined"
                          value={searchForm.captcha ?? ""}
                          onChange={(e) =>
                            handleSearchFormChange("captcha", e.target.value)
                          }
                          type="text"
                          placeholder={t("maxacnhan")}
                          className={`${
                            !isMobile ? "input-base-small" : "mobile-input-base"
                          } max-w-fit`}
                        />

                        <img
                          src={captchaUrl}
                          alt="captcha"
                          className="border rounded"
                        />

                        <button onClick={reloadCaptcha}>
                          <RotateCw className="w-5 h-5 cursor-pointer" />
                        </button>
                      </div>
                      {captchaInputError && (
                        <p className="error-text">{captchaInputError}</p>
                      )}
                    </div>

                    {errorMessage && (
                      <Alert
                        variant="error"
                        msg={errorMessage}
                        className="mt-2.5"
                      />
                    )}

                    <button
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-medium h-10 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                      onClick={handleSearch}
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <div className="flex items-center gap-2">
                          <Search className="w-5 h-5" />
                          <span>{t("timkiem")}</span>
                        </div>
                      )}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className={`${isMobile ? "pb-4" : ""} space-y-3`}>
                  {/* thong tin chung */}
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div className="lg:col-span-1">
                        <span className="block text-sm font-bold text-emerald-600 mb-1">
                          {t("hovatencod")}{" "}
                          <span className="text-red-500">*</span>
                        </span>
                        <input
                          {...register("name", {
                            onBlur: (e) => {
                              const formatted = toTitleCaseName(e.target.value);

                              setValue("name", formatted, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            },
                          })}
                          type="text"
                          placeholder="VD: Nguyễn Văn Bảo"
                          className={`${!isMobile ? "input-base-small" : "mobile-input-base"}`}
                        />
                        {errors.name && (
                          <p className="error-text">{errors.name.message}</p>
                        )}
                      </div>

                      <div className="lg:col-span-1">
                        {isMobile ? (
                          <div className="grid grid-cols-2 space-x-4">
                            <div>
                              <span className="block text-sm font-bold text-emerald-600 mb-1">
                                {t("ngaysinh")}{" "}
                                <span className="text-red-500">*</span>
                              </span>
                              <DatePickerCustom
                                name="dob"
                                control={control}
                                maxDateToday={true}
                                isMobile={isMobile}
                                allowTyping={true}
                              />
                              {errors.dob && (
                                <p className="error-text">
                                  {errors.dob.message}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-column">
                              <span className="block text-sm font-bold text-emerald-600 mb-2">
                                {t("gioitinh")}{" "}
                                <span className="text-red-500">*</span>
                              </span>

                              <div className="flex items-center gap-6 h-full">
                                {genderOptions.map((option) => (
                                  <label
                                    key={option.value}
                                    className="flex items-center gap-2"
                                  >
                                    <input
                                      type="radio"
                                      value={option.value}
                                      {...register("gender")}
                                      className="h-4 w-4"
                                    />
                                    <span className="text-sm text-slate-700">
                                      {option.label}
                                    </span>
                                  </label>
                                ))}
                              </div>

                              {errors.gender && (
                                <p className="error-text mt-1">
                                  {errors.gender.message}
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <span className="block text-sm font-bold text-emerald-600 mb-1">
                              {t("ngaysinh")}{" "}
                              <span className="text-red-500">*</span>
                            </span>
                            <DatePickerCustom
                              name="dob"
                              control={control}
                              maxDateToday={true}
                              isMobile={isMobile}
                              allowTyping={true}
                            />
                            {errors.dob && (
                              <p className="error-text">{errors.dob.message}</p>
                            )}
                          </div>
                        )}
                      </div>

                      {!isMobile && (
                        <div className="flex flex-column">
                          <label className="block text-sm font-bold text-emerald-600 mb-1">
                            {t("gioitinh")}{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <div className="flex items-center gap-6 h-full">
                            {genderOptions.map((option) => (
                              <label
                                key={option.value}
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                <input
                                  type="radio"
                                  value={option.value}
                                  {...register("gender")}
                                  className="h-4 w-4"
                                />
                                <span className="text-sm text-slate-700">
                                  {option.label}
                                </span>
                              </label>
                            ))}
                          </div>

                          {errors.gender && (
                            <p className="error-text">
                              {errors.gender.message}
                            </p>
                          )}
                        </div>
                      )}

                      <div>
                        <span className="block text-sm font-bold text-emerald-600 mb-1">
                          {t("sodienthoai")}{" "}
                          <span className="text-red-500">*</span>
                        </span>

                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center border-r border-gray-200 mt-2 mb-2">
                            <div className="flex items-center gap-2 pl-3 pr-3 ">
                              <span
                                className="fi fi-vn"
                                style={{
                                  width: 20,
                                  height: 20,
                                  display: "inline-block",
                                }}
                              />
                              <span className="text-gray-700 text-sm font-medium">
                                +84
                              </span>
                            </div>
                          </div>

                          <input
                            {...register("phone")}
                            type="tel"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoComplete="tel-national"
                            placeholder="Số điện thoại"
                            className={`${
                              !isMobile
                                ? "input-base-small pl-[90px]"
                                : "mobile-input-base pl-24"
                            }`}
                            style={
                              isMobile
                                ? {
                                    paddingTop: "0.7rem",
                                    paddingBottom: "0.7rem",
                                  }
                                : {}
                            }
                          />
                        </div>

                        {errors.phone && (
                          <p className="error-text">{errors.phone.message}</p>
                        )}
                      </div>

                      <div>
                        <span className="block text-sm font-bold text-emerald-600 mb-1">
                          {t("socccd")}{" "}
                        </span>

                        <input
                          {...register("idNumber", {
                            setValueAs: (v) =>
                              v
                                ? String(v)
                                    .toUpperCase()
                                    .replace(/[^A-Z0-9]/g, "")
                                    .slice(0, 12)
                                : "",
                            validate: (v) => {
                              const s = (v || "").toUpperCase().trim();
                              return (
                                isCCCD(s) ||
                                isCMND(s) ||
                                isVNPassport(s) ||
                                "CCCD 12 số, CMND 9 số, hoặc Passport dạng C1234567"
                              );
                            },
                          })}
                          type="text"
                          inputMode="text"
                          placeholder={t("socccd")}
                          pattern="[A-Za-z0-9]*"
                          autoComplete="off"
                          className={`${
                            !isMobile ? "input-base-small" : "mobile-input-base"
                          }`}
                          maxLength={12}
                          // onBeforeInput={(e) => {
                          //   const data =
                          //     (e as unknown as InputEvent).data ?? "";
                          //   if (data && /\D/.test(data)) e.preventDefault();
                          // }}
                          onChange={(e) => {
                            const t = e.currentTarget;
                            const cleaned = t.value
                              .toUpperCase()
                              .replace(/[^A-Z0-9]/g, "")
                              .slice(0, 12);
                            if (t.value !== cleaned) t.value = cleaned;
                          }}
                          onPaste={(e) => {
                            const data = (
                              e.clipboardData?.getData("text") || ""
                            )
                              .toUpperCase()
                              .replace(/[^A-Z0-9]/g, "")
                              .slice(0, 12);
                            e.preventDefault();
                            const t = e.currentTarget;
                            const start = t.selectionStart ?? t.value.length;
                            const end = t.selectionEnd ?? t.value.length;
                            const next = (
                              t.value.slice(0, start) +
                              data +
                              t.value.slice(end)
                            )
                              .toUpperCase()
                              .replace(/[^A-Z0-9]/g, "")
                              .slice(0, 12);
                            t.value = next;
                          }}
                          // onInput={(e) => {
                          //   const t = e.currentTarget;
                          //   const cleaned = t.value
                          //     .replace(/\D+/g, "")
                          //     .slice(0, 12);
                          //   if (t.value !== cleaned) t.value = cleaned;
                          // }}
                        />

                        {errors.idNumber && (
                          <p className="error-text">
                            {errors.idNumber.message}
                          </p>
                        )}
                      </div>

                      {!isMobile ? (
                        <div>
                          <span className="block font-bold text-emerald-600 mb-1">
                            {t("nghenghiep")}{" "}
                            <span className="text-red-500">*</span>
                          </span>
                          <FormSelect
                            name="job"
                            control={control}
                            options={jobOptions}
                            placeholder={t("nghenghiep")}
                          />

                          {errors.job && (
                            <p className="error-text">{errors.job.message}</p>
                          )}
                        </div>
                      ) : (
                        <div>
                          <span className="block text-sm font-bold text-emerald-600 mb-1">
                            {t("nghenghiep")}{" "}
                            <span className="text-red-500">*</span>
                          </span>
                          <RHFMobileSelectSheet
                            control={control}
                            name="job"
                            options={jobOptions}
                            label={t("chonnghenghiep")}
                            placeholder={t("chonnghenghiep")}
                            rules={{ required: t("vulongchonnghenghiep") }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* <h3 className="text-base font-semibold text-gray-800 mb-3">Địa chỉ theo CCCD</h3> */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {!isMobile ? (
                      <div>
                        <span className="block text-sm font-bold text-emerald-600 mb-1">
                          {t("dantoc")} <span className="text-red-500">*</span>
                        </span>
                        <FormSelect
                          name="ethnicity"
                          control={control}
                          options={ethnicOptions}
                          placeholder={t("dantoc")}
                        />
                        {errors.ethnicity && (
                          <p className="error-text">
                            {errors.ethnicity.message}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <span className="block text-sm font-bold text-emerald-600 mb-1">
                          {t("dantoc")} <span className="text-red-500">*</span>
                        </span>
                        <RHFMobileSelectSheet
                          control={control}
                          name="ethnicity"
                          options={ethnicOptions}
                          label={t("chondantoc")}
                          placeholder={t("dantoc")}
                        />
                      </div>
                    )}

                    {!isMobile ? (
                      <div>
                        <span className="block text-sm font-bold text-emerald-600 mb-1">
                          {t("tinhthanh")}
                        </span>
                        <FormSelect
                          name="city"
                          control={control}
                          options={provinceOptions}
                          placeholder={t("tinhthanh")}
                          onChange={(opt) => onProvinceSelected(opt)}
                        />

                        {errors.city && (
                          <p className="error-text">{errors.city.message}</p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <span className="block text-sm font-bold text-emerald-600 mb-1">
                          {t("tinhthanh")}
                        </span>
                        <RHFMobileSelectSheet
                          control={control}
                          name="city"
                          options={provinceOptions}
                          label={t("chontinhthanh")}
                          placeholder={t("chontinhthanh")}
                          rules={{ required: t("vulongchontinhthanh") }}
                          onSelect={(opt) => onProvinceSelected(opt)}
                        />
                      </div>
                    )}

                    {!isMobile ? (
                      <div>
                        <span className="block text-sm font-bold text-emerald-600 mb-1">
                          {t("phuongxa")}
                        </span>
                        <FormSelect
                          name="ward"
                          control={control}
                          options={wardOptions}
                          placeholder={t("phuongxa")}
                        />
                        {errors.ward && (
                          <p className="error-text">{errors.ward.message}</p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <span className="block md:text-base text-sm font-bold text-emerald-600 mb-1">
                          {t("phuongxa")}
                        </span>
                        <RHFMobileSelectSheet
                          control={control}
                          name="ward"
                          options={wardOptions}
                          label={t("chonphuongxa")}
                          placeholder={t("chonphuongxa")}
                          rules={{ required: t("vulongchonphuongxa") }}
                        />
                      </div>
                    )}

                    <div className="lg:col-span-2">
                      <span className="block text-sm font-bold mb-1 text-emerald-600">
                        {t("lydokham")} <span className="text-red-500">*</span>
                      </span>
                      <textarea
                        {...lydokhamRegister}
                        ref={(el) => {
                          lydokhamRegister.ref(el);
                          lydokhamRef.current = el;
                        }}
                        maxLength={500}
                        placeholder={t("nhaplydokham")}
                        className={`h-[90px] ${
                          !isMobile ? "input-base-small" : "mobile-input-base"
                        }`}
                      />
                      {errors.lydokham && (
                        <p className="error-text">{errors.lydokham.message}</p>
                      )}
                    </div>
                  </div>

                  {/* CaptchaComponent */}
                  {/* <div className="mt-3">
                    <span className="block text-sm font-bold text-emerald-600 mb-2">
                      {t("maxacnhan")} <span className="text-red-500">*</span>
                    </span>

                    <div className="flex items-center gap-3">
                      <input
                        key="new"
                        {...register("captchaInput")}
                        type="text"
                        placeholder={t("nhapmaxacnhan")}
                        className={`${
                          !isMobile ? "input-base-small" : "mobile-input-base"
                        } max-w-fit`}
                      />

                      <img
                        src={captchaUrl}
                        alt="captcha"
                        className="border rounded"
                      />

                      <button onClick={reloadCaptcha}>
                        <RotateCw className="w-5 h-5 cursor-pointer" />
                      </button>
                    </div>
                    {errors.captchaInput && (
                      <p className="error-text">
                        {errors.captchaInput.message}
                      </p>
                    )}
                  </div> */}

                  {errorMessage && (
                    <Alert
                      variant="error"
                      msg={errorMessage}
                      className="mt-2.5"
                    />
                  )}
                </div>
                {/* desktop */}
                <div className="hidden md:flex items-center justify-center mt-8">
                  <button
                    className={`max-w-[250px] w-full flex justify-center button-primary text-white py-3 rounded-full font-normal text-base transition-colors`}
                    onClick={() => {
                      if (checkBookingTime()) handleSubmit(onSubmit)();
                    }}
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <span>{t("dangkythongtin")}</span>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {activeTab === "new" && (
            <div
              className="h-[70px] md:hidden fixed bottom-0 bg-white w-full md:p-4 p-3 border-t border-gray-200 shadow-lg
              flex items-center justify-center gap-4"
            >
              <QrScanButton onDecoded={handleDecoded} />

              <button
                className={`w-full h-full flex items-center justify-center button-primary text-white py-3 rounded-full font-normal text-base transition-colors`}
                onClick={() => {
                  if (checkBookingTime()) handleSubmit(onSubmit)();
                }}
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <div className="flex items-center gap-2">
                    <span>{t("dangkythongtin")}</span>
                  </div>
                )}
              </button>
            </div>
          )}

          {/* {activeTab === "examined" && searchResult && (
            <div
              className="h-[70px] flex items-center justify-center md:hidden fixed bottom-0 bg-white w-full p-4 border-t border-gray-200 shadow-lg 
              flex gap-2 op-0 animate-slide-up-delay-2"
            >
              <button
                className="w-full h-full basis-1/3 bg-gray-400 hover:bg-gray-500 text-white font-medium py-2.5 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                onClick={cancelSearchResult}
              >
                <span>{t("quaylai")}</span>
              </button>

              <button
                className="basis-2/3 h-full w-full flex items-center justify-center bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                onClick={handleSubmitExamined}
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    <span>{t("dangkythongtin")}</span>
                  </div>
                )}
              </button>
            </div>
          )} */}
        </div>
      </div>
      <BookingTimeoutDialog
        isOpen={dialogOpen}
        type={dialogType}
        onClose={closeDialog}
      />
    </div>
  );
}

export default EnterInfoStep;
